import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, Check } from 'lucide-react';
import { verifyEmailWithToken } from '../api/client';
import { useAuth } from '../context/AuthContext';

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { user, updateUser } = useAuth();

  const [state, setState] = useState<'pending' | 'success' | 'error'>('pending');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState('error');
      setError('Missing verification token.');
      return;
    }
    let cancelled = false;
    verifyEmailWithToken(token)
      .then(() => {
        if (cancelled) return;
        // Update the cached AuthUser so the Account page's "unverified"
        // banner clears immediately — the row is already flipped in the DB
        // by the time the API returns 200, the cache just doesn't know yet.
        if (user && !user.is_verified) {
          updateUser({ is_verified: true });
        }
        setState('success');
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setError(detail || 'Verification link is invalid or has expired.');
        setState('error');
      });
    return () => {
      cancelled = true;
    };
    // We deliberately don't depend on `user` / `updateUser` — this should
    // run exactly once per token in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className="card p-6 text-center">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Verify your email</h1>
        {state === 'pending' && (
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-gray-400" />
        )}
        {state === 'success' && (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-3">
              <Check className="w-6 h-6 text-green-700 dark:text-green-300" />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">Your email is verified.</p>
            <Link
              to="/account"
              className="inline-block mt-4 px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full"
            >
              Go to account
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-3">
              <AlertCircle className="w-6 h-6 text-red-700 dark:text-red-300" />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">{error}</p>
            <Link
              to="/account"
              className="inline-block mt-4 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Back to account
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
